import { useEffect, useMemo, useState } from 'react'
import { usePlatform } from '../platform'
import { useConversations } from '../data/hooks'
import { useWorkspace } from '../data/workspace'
import { useSidebar } from '../state/sidebar'
import { useUi } from '../state/ui'
import { applyProposals } from '../sidebar/layout'
import { Icon } from './Icon'
import { Emoji, Modal, Spinner } from './common'

interface Proposed {
  name: string
  emoji?: string
  channels: string[]
}

type State = { phase: 'loading' } | { phase: 'error'; message: string } | { phase: 'ready'; sections: Proposed[] }

const close = () => useUi.getState().openModal(null)

const ERRORS: Record<string, string> = {
  ai_not_configured: 'AI isn’t set up on the Assembly server yet.',
  rate_limited: 'You’ve organized a lot recently. Try again in a bit.',
  ai_busy: 'The AI is busy right now. Try again in a minute.',
  not_enough_channels: 'There aren’t enough unsorted channels to organize.',
}

/** "Organize with AI": proposes sections for channels that aren't in one yet; nothing changes until you accept. */
export function AiOrganizeModal() {
  const platform = usePlatform()
  const { account } = useWorkspace()
  const { data: convs } = useConversations()
  const layout = useSidebar((s) => s.layout)
  const edit = useSidebar((s) => s.edit)
  const [state, setState] = useState<State>({ phase: 'loading' })
  const [skipped, setSkipped] = useState<Set<number>>(new Set())
  const [attempt, setAttempt] = useState(0)

  // Only channels you haven't sorted yet, so your existing sections are left alone.
  const unsorted = useMemo(() => {
    const placed = new Set(layout.sections.flatMap((s) => s.items))
    return (convs ?? []).filter((c) => !c.is_im && !c.is_mpim && !c.is_archived && !placed.has(c.id))
  }, [convs, layout])
  const byId = useMemo(() => new Map((convs ?? []).map((c) => [c.id, c] as const)), [convs])

  useEffect(() => {
    if (!convs) return
    if (!platform.aiUrl) return setState({ phase: 'error', message: ERRORS.ai_not_configured })
    if (unsorted.length < 2) return setState({ phase: 'error', message: ERRORS.not_enough_channels })
    let cancelled = false
    setState({ phase: 'loading' })
    setSkipped(new Set())
    fetch(`${platform.aiUrl}/organize`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${account.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        channels: unsorted.map((c) => ({ id: c.id, name: c.name, topic: c.topic?.value, purpose: c.purpose?.value, members: c.num_members, private: c.is_private })),
        existing: layout.sections.map((s) => s.name),
      }),
    })
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as { sections?: Proposed[]; error?: string }
        if (cancelled) return
        if (!res.ok || !body.sections) return setState({ phase: 'error', message: ERRORS[body.error ?? ''] ?? 'Couldn’t organize your channels. Please try again.' })
        if (!body.sections.length) return setState({ phase: 'error', message: 'The AI didn’t find any useful groups for these channels.' })
        setState({ phase: 'ready', sections: body.sections })
      })
      .catch(() => !cancelled && setState({ phase: 'error', message: 'Couldn’t reach the Assembly server.' }))
    return () => {
      cancelled = true
    }
    // Run once per open (and on Retry), not when the layout changes underneath.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convs === undefined, attempt])

  const accepted = state.phase === 'ready' ? state.sections.filter((_, i) => !skipped.has(i)) : []
  const apply = () => {
    if (!accepted.length) return
    edit((l) => applyProposals(l, accepted))
    useUi.getState().showToast(`Added ${accepted.length} ${accepted.length === 1 ? 'section' : 'sections'}`)
    close()
  }

  return (
    <Modal
      title="Organize with AI"
      onClose={close}
      wide
      footer={
        <>
          <button type="button" className="button" onClick={close}>
            Cancel
          </button>
          {state.phase === 'error' && (
            <button type="button" className="button" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
          )}
          {state.phase === 'ready' && (
            <button type="button" className="button primary" disabled={!accepted.length} onClick={apply}>
              Add {accepted.length} {accepted.length === 1 ? 'section' : 'sections'}
            </button>
          )}
        </>
      }
    >
      {state.phase === 'loading' && (
        <div className="ai-organize-loading">
          <Spinner size={22} />
          <span>Sorting {unsorted.length} channels into sections…</span>
        </div>
      )}
      {state.phase === 'error' && <p className="ai-organize-error">{state.message}</p>}
      {state.phase === 'ready' && (
        <>
          <p className="muted ai-organize-intro">Suggested sections for channels you haven’t sorted yet. Untick any you don’t want. Anything left over stays in Channels.</p>
          <ul className="ai-organize-list">
            {state.sections.map((s, i) => {
              const on = !skipped.has(i)
              return (
                <li key={i} className={on ? '' : 'off'}>
                  <label>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() =>
                        setSkipped((prev) => {
                          const next = new Set(prev)
                          if (next.has(i)) next.delete(i)
                          else next.add(i)
                          return next
                        })
                      }
                    />
                    <span className="ai-organize-name">
                      {s.emoji ? <Emoji name={s.emoji} size={16} /> : <Icon name="folder" size={15} />}
                      <strong>{s.name}</strong>
                      <span className="muted">{s.channels.length}</span>
                    </span>
                  </label>
                  <div className="ai-organize-channels">
                    {s.channels.map((id) => (
                      <span key={id} className="ai-organize-chip">
                        <Icon name={byId.get(id)?.is_private ? 'lock' : 'hash'} size={12} />
                        {byId.get(id)?.name ?? id}
                      </span>
                    ))}
                  </div>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </Modal>
  )
}
